import { Box, Stack } from "@mui/material";
import { ProgressCarousel } from "@src/appV2/redesign/components/ProgressCarousel";
import { TopLeftBackButton } from "@src/appV2/redesign/components/TopLeftBackButton";
import { CbhIcon, Text } from "@src/appV2/redesign/system/ui-components";

import { EducationStepNavigation } from "./EducationStepNavigation";

interface ShiftCategoryTagProps {
  type?: "Priority";
}

function ShiftCategoryTag(props: ShiftCategoryTagProps) {
  const { type = "Priority" } = props;

  if (type === "Priority") {
    return (
      <Box
        sx={(theme) => ({
          display: "inline-flex",
          alignItems: "center",
          gap: theme.spacing(1),
          paddingX: theme.spacing(2),
          paddingY: theme.spacing(1),
          backgroundColor: theme.palette.background.quaternary,
          border: `1px solid ${theme.palette.shift?.priority.border}`,
          borderRadius: "100px",
          fontSize: "12px",
          fontWeight: 500,
          color: theme.palette.shift?.priority.text,
          lineHeight: "14.5px",
        })}
      >
        <CbhIcon
          type="priority"
          size="xSmall"
          // center it relative to the text
          sx={(theme) => ({
            color: theme.palette.shift?.priority.text,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transform: "translateY(-1px)",
          })}
        />
        Priority
      </Box>
    );
  }

  return null;
}

interface HigherScoreIsBetterStepProps {
  shiftQualification: string;
  onNext: () => void;
  onBack: () => void;
  progressCarousel?: {
    currentIndex: number;
    totalSteps: number;
    onIndexChange: (index: number) => void;
  };
}

export function HigherScoreIsBetterStep(props: HigherScoreIsBetterStepProps) {
  const { onNext, onBack, shiftQualification, progressCarousel } = props;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ width: "100%" }}>
        <TopLeftBackButton iconType="chevron-left" onClick={onBack} />
      </Box>

      <Box
        sx={{
          width: "100%",
          height: "100%",
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "35%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "100%",
            paddingX: 4,
          }}
        >
          <Box
            sx={(theme) => ({
              backgroundColor: theme.palette.background.paper,
              borderRadius: "24px",
              padding: theme.spacing(6),
              boxShadow: "0px 4px 20px 0px rgba(0,0,0,0.05)",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: theme.spacing(2.625),
            })}
          >
            <Stack spacing={4}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <ShiftCategoryTag type="Priority" />
              </Box>
              <Stack spacing={4}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "end",
                  }}
                >
                  <Text
                    sx={(theme) => ({
                      fontSize: "16px",
                      fontWeight: 500,
                      color: theme.palette.text.primary,
                      lineHeight: "14.5px",
                    })}
                  >
                    9:00 am - 5:00 pm
                  </Text>
                  <Text
                    sx={(theme) => ({
                      fontSize: "14px",
                      fontWeight: 500,
                      color: theme.palette.text.tertiary,
                      lineHeight: "17px",
                    })}
                  >
                    8h
                  </Text>
                </Box>
                <Box sx={{ display: "flex", gap: 2, height: "14px", alignItems: "center" }}>
                  <Text
                    sx={(theme) => ({
                      fontSize: "14px",
                      color: theme.palette.text.secondary,
                      lineHeight: "17px",
                    })}
                  >
                    {shiftQualification}
                  </Text>
                </Box>
              </Stack>
            </Stack>
          </Box>
        </Box>

        <Box
          sx={{
            position: "absolute",
            top: "58%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
          }}
        >
          <Stack spacing={6} alignItems="center" sx={{ width: "100%", maxWidth: 343 }}>
            <Text
              variant="h4"
              sx={(theme) => ({
                fontSize: "32px",
                color: theme.palette.text.primary,
                lineHeight: "35px",
                textAlign: "center",
              })}
            >
              Higher scores = better opportunities
            </Text>
            <Text
              variant="body1"
              sx={(theme) => ({
                color: theme.palette.text.secondary,
                fontSize: "16px",
                lineHeight: "17px",
                textAlign: "center",
              })}
            >
              With a higher score, you&apos;ll earn Priority Access, giving you first dibs on the
              best opportunities close to home.
            </Text>
          </Stack>
        </Box>

        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
          }}
        >
          <Stack spacing={3} sx={{ width: "100%" }}>
            {progressCarousel && (
              <Box sx={{ paddingX: 2 }}>
                <ProgressCarousel
                  currentIndex={progressCarousel.currentIndex}
                  totalCount={progressCarousel.totalSteps}
                  onIndexChange={progressCarousel.onIndexChange}
                />
              </Box>
            )}
            <EducationStepNavigation onNext={onNext} onBack={onBack} />
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
